import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Plus, Trash2, Camera, PawPrint, Phone, 
  Calendar, Stethoscope, FileText, Loader2, X, Edit2, CloudOff
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { haptics } from '@/utils/haptics';
import useOfflineFirst, { STORES } from '@/hooks/useOfflineFirst';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';
import { getSyncStatus } from '@/services/offlineDataService';
import { ProtectedFeaturePage } from '@/components/ProtectedFeaturePage';
import { useDemo } from '@/contexts/DemoContext';

/**
 * PetInfoPage - Store important information about pets
 * PREMIUM FEATURE - Requires Premium subscription
 * 
 * Allows users to add and manage pet profiles with:
 * - Pet name, photo, breed, birth date
 * - Veterinarian contact info
 * - Notes
 */

const PetInfoContent = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDemo();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const fileInputRef = useRef(null);
  
  // Offline-first data management
  const { 
    fetchWithCache, 
    saveWithOfflineSupport, 
    deleteWithOfflineSupport,
    isOfflineAvailable 
  } = useOfflineFirst('pets');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    breed: '',
    birth_date: '',
    vet_name: '',
    vet_phone: '',
    notes: ''
  });

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      
      // In demo mode, use empty array (no pets in demo)
      if (isDemoMode) {
        setPets([]);
        setFromCache(false);
        setLoading(false);
        return;
      }
      
      // Use offline-first fetching for real users
      const result = await fetchWithCache('/pets', { idKey: 'id' });
      setPets(result.data || []);
      setFromCache(result.fromCache || false);
      
      if (result.fromCache) {
        toast.info('Showing cached data', { 
          description: 'You may be offline. Data will sync when connected.',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Failed to fetch pets:', error);
      setPets([]);
      if (!navigator.onLine) {
        toast.error('You are offline and no cached data is available');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithCache, isDemoMode]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  // Track pending sync changes
  useEffect(() => {
    const checkPendingChanges = () => {
      const status = getSyncStatus();
      setHasPendingChanges(status.hasPendingChanges);
    };
    
    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 5000);
    return () => clearInterval(interval);
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      photo: '',
      breed: '',
      birth_date: '',
      vet_name: '',
      vet_phone: '',
      notes: ''
    });
    setEditingPet(null);
  };

  const handleAddPet = () => {
    haptics.tap();
    resetForm();
    setShowAddForm(true);
  };

  const handleEditPet = (pet) => {
    haptics.tap();
    setFormData({
      name: pet.name || '',
      photo: pet.photo || '',
      breed: pet.breed || '',
      birth_date: pet.birth_date || '',
      vet_name: pet.vet_name || '',
      vet_phone: pet.vet_phone || '',
      notes: pet.notes || ''
    });
    setEditingPet(pet);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    haptics.tap();
    setShowAddForm(false);
    resetForm();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    try {
      // Convert to base64 for simple storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your pet\'s name');
      return;
    }

    try {
      setSaving(true);
      haptics.tap();

      if (editingPet) {
        // Update existing pet with offline support
        const result = await saveWithOfflineSupport(`/pets/${editingPet.id}`, formData, 'PUT');
        
        if (result.queued) {
          toast.success('Pet updated (will sync when online)', {
            description: 'Changes saved locally',
            icon: <CloudOff className="h-4 w-4" />
          });
        } else {
          toast.success('Pet updated');
        }
      } else {
        // Create new pet with offline support
        const result = await saveWithOfflineSupport('/pets', formData, 'POST');
        
        if (result.queued) {
          toast.success('Pet added (will sync when online)', {
            description: 'Changes saved locally',
            icon: <CloudOff className="h-4 w-4" />
          });
        } else {
          toast.success('Pet added');
        }
      }

      setShowAddForm(false);
      resetForm();
      fetchPets();
    } catch (error) {
      console.error('Failed to save pet:', error);
      toast.error('Failed to save pet');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (petId) => {
    if (!confirm('Are you sure you want to remove this pet?')) return;

    try {
      haptics.tap();
      const result = await deleteWithOfflineSupport(`/pets/${petId}`, petId);
      
      if (result.queued) {
        toast.success('Pet removed (will sync when online)', {
          description: 'Changes saved locally',
          icon: <CloudOff className="h-4 w-4" />
        });
      } else {
        toast.success('Pet removed');
      }
      
      // Update local state immediately
      setPets(prev => prev.filter(p => p.id !== petId));
    } catch (error) {
      toast.error('Failed to remove pet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="pet-info-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Pet Info</h1>
              {fromCache && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <CloudOff className="h-3 w-3" />
                  Offline mode
                </p>
              )}
            </div>
            {pets.length > 0 && !showAddForm && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddPet}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Pet
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Sync Status Banner - shows when there are pending changes or offline */}
      {(hasPendingChanges || fromCache) && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <SyncStatusIndicator variant="full" size="md" />
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Empty State */}
        {pets.length === 0 && !showAddForm && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
              <PawPrint className="h-10 w-10 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">Pet Info</h2>
            <p className="text-slate-500 mb-8 max-w-sm mx-auto leading-relaxed">
              Store important information about your pets so caregivers or veterinarians have what they need in an emergency.
            </p>
            <Button
              onClick={handleAddPet}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
              data-testid="add-pet-btn"
            >
              <Plus className="h-5 w-5" />
              Add Pet
            </Button>
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="border-0 shadow-sm mb-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <PawPrint className="h-5 w-5 text-amber-500" />
                  {editingPet ? 'Edit Pet' : 'Add New Pet'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCancel}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo Upload */}
              <div className="flex flex-col items-center gap-3">
                <div 
                  className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300 cursor-pointer hover:border-amber-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {formData.photo ? (
                    <img 
                      src={formData.photo} 
                      alt="Pet" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Camera className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-amber-600 hover:text-amber-700"
                >
                  {formData.photo ? 'Change Photo' : 'Add Photo'}
                </button>
              </div>

              {/* Pet Name */}
              <div className="space-y-2">
                <Label htmlFor="pet-name">Pet Name *</Label>
                <Input
                  id="pet-name"
                  placeholder="e.g., Max, Bella, Whiskers"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="pet-name-input"
                />
              </div>

              {/* Breed */}
              <div className="space-y-2">
                <Label htmlFor="pet-breed">Breed</Label>
                <Input
                  id="pet-breed"
                  placeholder="e.g., Golden Retriever, Tabby Cat"
                  value={formData.breed}
                  onChange={(e) => setFormData(prev => ({ ...prev, breed: e.target.value }))}
                />
              </div>

              {/* Birth Date */}
              <div className="space-y-2">
                <Label htmlFor="pet-birthdate" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  Birth Date
                </Label>
                <Input
                  id="pet-birthdate"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                />
              </div>

              {/* Veterinarian Info */}
              <div className="pt-2 border-t border-slate-100">
                <Label className="flex items-center gap-2 mb-3 text-slate-700">
                  <Stethoscope className="h-4 w-4 text-slate-400" />
                  Veterinarian
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="vet-name" className="text-xs text-slate-500">Vet Name</Label>
                    <Input
                      id="vet-name"
                      placeholder="Dr. Smith"
                      value={formData.vet_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, vet_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vet-phone" className="text-xs text-slate-500">Vet Phone</Label>
                    <Input
                      id="vet-phone"
                      placeholder="(555) 123-4567"
                      value={formData.vet_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, vet_phone: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="pet-notes" className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  Notes
                </Label>
                <Textarea
                  id="pet-notes"
                  placeholder="Allergies, medications, special needs, microchip number..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    editingPet ? 'Save Changes' : 'Add Pet'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pet List */}
        {pets.length > 0 && !showAddForm && (
          <div className="space-y-4">
            {pets.map((pet) => (
              <Card key={pet.id} className="border-0 shadow-sm overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Pet Photo */}
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {pet.photo ? (
                        <img 
                          src={pet.photo} 
                          alt={pet.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <PawPrint className="h-8 w-8 text-amber-400" />
                      )}
                    </div>

                    {/* Pet Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 text-lg">{pet.name}</h3>
                      {pet.breed && (
                        <p className="text-sm text-slate-500">{pet.breed}</p>
                      )}
                      {pet.birth_date && (
                        <p className="text-xs text-slate-400 mt-1">
                          Born: {new Date(pet.birth_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditPet(pet)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(pet.id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Vet Info */}
                  {(pet.vet_name || pet.vet_phone) && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4">
                      <Stethoscope className="h-4 w-4 text-slate-400" />
                      <div className="flex-1">
                        {pet.vet_name && (
                          <p className="text-sm font-medium text-slate-700">{pet.vet_name}</p>
                        )}
                        {pet.vet_phone && (
                          <a 
                            href={`tel:${pet.vet_phone}`}
                            className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                          >
                            <Phone className="h-3 w-3" />
                            {pet.vet_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {pet.notes && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-sm text-slate-600">{pet.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// Wrap content with feature protection - Premium tier required
const PetInfoPage = () => (
  <ProtectedFeaturePage
    feature="pet_info"
    title="Pet Information"
    description="Keep all your pet's important details in one place - from vet contacts to emergency care instructions."
    backPath="/"
  >
    <PetInfoContent />
  </ProtectedFeaturePage>
);

export default PetInfoPage;
